/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, window.location usage, and Chakra UI components
 * - Enhanced authentication validation to check token expiry upfront
 */

'use client'

import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import qs from 'qs'
import _ from 'lodash'
import {Center, Flex, Heading, Spinner, Text} from '@chakra-ui/react'
import moment from 'moment'

// Fix: Create a separate function for authorize URL construction
function composeOauth2AuthorizeUrl(authorizeUrlBase, {clientId, redirectUri, scope = 'profile,email', state = null, error = null}) {
  return `${authorizeUrlBase}${qs.stringify(
    {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      ...(state && {state}),
      ...(error && {error})
    },
    {
      addQueryPrefix: true
    }
  )}`
}

function composeOauth2LoginUrl(authenticateUrlPath, {clientId, redirectUri, scope = 'profile,email', state = null, error = null}) {
  return `${authenticateUrlPath}${qs.stringify(
    {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      ...(state && {state}),
      ...(error && {error})
    },
    {
      addQueryPrefix: true
    }
  )}`
}

// Base permission checker function
function checkBasePermission(currentUser, scopes) {
  if (!currentUser) {
    return null
  }

  const isRequireScope = !!scopes?.length

  if (!isRequireScope) {
    return true
  }

  // Check if user has any of the required scopes
  const intersection = _.intersection(currentUser.scope, scopes)
  const hasPermission = !!intersection.length

  return hasPermission
}

/**
 * Enhanced authentication check that validates both token existence and expiry
 * This prevents the UI from showing as "authenticated" when tokens are expired
 */
function isValidlyAuthenticated(oauthCredentials) {
  // Check if we have an access token
  if (!oauthCredentials.accessToken) {
    return false
  }

  // Check if token has expiry information
  if (!oauthCredentials.expiresAt) {
    console.warn('[AuthorizedProvider] Token exists but no expiry time - treating as valid')
    return true
  }

  // Check if token is still valid (not expired)
  const tokenExpiresAt = moment.utc(oauthCredentials.expiresAt)
  const now = moment.utc()
  const isExpired = tokenExpiresAt.isBefore(now)

  if (isExpired) {
    console.debug('[AuthorizedProvider] Token is expired - redirecting to login immediately')
    console.debug(`[AuthorizedProvider] Token expired at: ${tokenExpiresAt.format()}, current time: ${now.format()}`)
  }

  return !isExpired
}

const AuthorizedProvider = ({
  authenticateUrlPath,
  authorizeUrlBase,
  children,
  clientId,
  currentUser,
  currentLendingUser,
  oauthCredentials,
  redirectUri,
  scope = 'profile,email',
  scopes,
  state = null,
  permissionChecker = null
}) => {
  // Use enhanced authentication check that validates token expiry upfront
  const isAuthenticated = isValidlyAuthenticated(oauthCredentials)

  // Create the authorize URL base - either from authorizeUrlBase or fallback to constructed path
  const authorizeUrlPrefix = authorizeUrlBase ? `${authorizeUrlBase}/oauth/authorize` : authenticateUrlPath.replace(/\/login$/, '/oauth/authorize')

  // For login, use the authenticateUrlPath (UI)
  const authenticateUrl = composeOauth2LoginUrl(authenticateUrlPath, {
    clientId,
    redirectUri,
    scope,
    state
  })

  const isPermitted = permissionChecker ? permissionChecker(currentUser, scopes, {currentLendingUser}) : checkBasePermission(currentUser, scopes)

  useEffect(() => {
    if (!isAuthenticated) {
      console.debug('[AuthorizedProvider] Not authenticated or token expired - redirecting to login')
      window.location.replace(authenticateUrl)
    }
  }, [isAuthenticated, authenticateUrl])

  if (!isAuthenticated) {
    return (
      <Center
        position="fixed"
        zIndex={10000}
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 1)"
        bgColor="primary.500"
        h="100vh"
        w="100vw"
        flexDirection="column"
        justify="center"
        align="center"
        gap={4}>
        <Spinner size="xl" fontSize="2xl" color="white" />
        <Text color="white" fontSize="xl" fontWeight="bold">
          Loading...
        </Text>
      </Center>
    )
  }

  return (
    <>
      {(() => {
        if (isPermitted === false) {
          return (
            <Flex direction="column" flex="1" align="center" justify="center" bgColor="neutral.100">
              <Heading as="h1" size="xl" mb={4}>
                Not Permitted
              </Heading>
              <Text>You are not permitted to access to this page.</Text>
            </Flex>
          )
        } else if (isAuthenticated && isPermitted === true) {
          return children
        } else {
          return null
        }
      })()}
    </>
  )
}

AuthorizedProvider.propTypes = {
  authenticateUrlPath: PropTypes.string.isRequired,
  authorizeUrlBase: PropTypes.string,
  children: PropTypes.any,
  clientId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    scope: PropTypes.arrayOf(PropTypes.string).isRequired
  }),
  currentLendingUser: PropTypes.shape({
    is_organization_manager: PropTypes.bool,
    is_borrower: PropTypes.bool,
    is_lender: PropTypes.bool,
    scope: PropTypes.arrayOf(PropTypes.string)
  }),
  oauthCredentials: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    expiresAt: PropTypes.string
  }).isRequired,
  redirectUri: PropTypes.string.isRequired,
  scope: PropTypes.string,
  scopes: PropTypes.array,
  state: PropTypes.string,
  permissionChecker: PropTypes.func
}

export {AuthorizedProvider, composeOauth2LoginUrl, composeOauth2AuthorizeUrl, checkBasePermission}
